/**
 * Despieces de muestra.
 *
 * ⚠️ **Ninguno de estos números viene del taller.** Son aproximaciones razonables,
 * puestas para que la interfaz de captura exista, se pueda editar y se vea que el
 * cálculo funciona de punta a punta. Cada medida, cada minuto y cada herraje se
 * corrige desde la pantalla de Configuración, sin tocar código.
 *
 * Se eligieron tres piezas a propósito, porque recorren tres caminos de fabricación
 * distintos y demuestran que el modelo aguanta la variedad real del taller:
 *
 *  · **Cajonera** — tablero, router, canteado, herrajes y armado. Producto discreto:
 *    consume placas enteras.
 *  · **Repisa flotante metálica** — lámina, láser, doblez, soldadura y pintura.
 *    Otro material, otra cadena de procesos, sin canto ni herraje de mueble.
 *  · **Celosía de fachada** — se vende por metro cuadrado, así que su material se prorratea,
 *    y su costo lo domina el corte, no la placa.
 *
 * Ese último caso es el que mejor explica el modelo: **una celosía y un panel liso
 * del mismo material cuestan casi lo mismo de placa y diez veces distinto de
 * máquina.** Por eso el corte se calcula por metros de trayectoria y no por área.
 */

export const DESPIECES_MUESTRA = {

  /* ------------------------------------------------------------------ */
  /* Cajonera de tres cajones — 450 × 600 × 500 mm, MDF de 18 y 6 mm     */
  /* ------------------------------------------------------------------ */
  'TZ-CAJONERA-3': {
    nombre: 'Cajonera de 3 cajones',
    medidas: '450 ancho × 600 alto × 500 fondo',
    aprovechamiento: 0.78,
    piezas: [
      { nombre: 'Costado',            material: 'MDF18', largo: 600, ancho: 500, cant: 2, cantoLargo: 1 },
      { nombre: 'Tapa',               material: 'MDF18', largo: 450, ancho: 500, cant: 1, cantoLargo: 1 },
      { nombre: 'Base',               material: 'MDF18', largo: 450, ancho: 500, cant: 1 },
      { nombre: 'Frente de cajón',    material: 'MDF18', largo: 430, ancho: 185, cant: 3, cantoLargo: 2, cantoAncho: 2 },
      { nombre: 'Lateral de cajón',   material: 'MDF18', largo: 450, ancho: 120, cant: 6 },
      { nombre: 'Testero de cajón',   material: 'MDF18', largo: 380, ancho: 120, cant: 6 },
      { nombre: 'Trasero',            material: 'MDF6',  largo: 450, ancho: 600, cant: 1 },
      { nombre: 'Fondo de cajón',     material: 'MDF6',  largo: 380, ancho: 450, cant: 3 }
    ],
    herrajes: [
      { sku: 'HER-CORREDERA45', cant: 3 },
      { sku: 'HER-TIRADOR',     cant: 3 },
      { sku: 'HER-TORNILLO',    cant: 40 },
      { sku: 'HER-TARUGO',      cant: 24 },
      { sku: 'HER-NIVELADOR',   cant: 4 }
    ],
    procesos: [
      { proceso: 'armado',  minutos: 55 },
      { proceso: 'empaque', minutos: 10 }
    ]
  },

  /* ------------------------------------------------------------------ */
  /* Repisa flotante metálica — 900 × 250 mm, lámina cal. 16 doblada     */
  /* ------------------------------------------------------------------ */
  'TZ-REPISA-FLOTANTE-METALICA': {
    nombre: 'Repisa flotante metálica',
    medidas: '900 largo × 250 fondo × 60 alto',
    aprovechamiento: 0.72,
    dobleces: 4,
    piezas: [
      { nombre: 'Cuerpo desarrollado', material: 'LAM16', largo: 900, ancho: 430, cant: 1 },
      { nombre: 'Tapa lateral',        material: 'LAM16', largo: 250, ancho: 60,  cant: 2 },
      { nombre: 'Placa de anclaje',    material: 'LAM16', largo: 860, ancho: 80,  cant: 1, corteExtraM: 0.4 }
    ],
    herrajes: [
      { sku: 'HER-TAQUETE', cant: 4 },
      { sku: 'HER-PINTURA', cant: 0.7 }
    ],
    procesos: [
      { proceso: 'soldadura', minutos: 12 },
      { proceso: 'pintura',   minutos: 9 },
      { proceso: 'armado',    minutos: 8 },
      { proceso: 'empaque',   minutos: 6 }
    ]
  },

  /* ------------------------------------------------------------------ */
  /* Celosía de fachada — despiece de UN metro cuadrado, material prorrateado */
  /* ------------------------------------------------------------------ */
  'TZ-CELOSIA-DE-FACHADA': {
    nombre: 'Celosía de fachada',
    medidas: 'por m², patrón calado',
    aprovechamiento: 0.82,
    prorratear: true,   // se vende por m²: la placa se reparte en el pedido
    piezas: [
      {
        nombre: 'Panel calado', material: 'LAM14', largo: 1000, ancho: 1000, cant: 1,
        // Aquí está el punto del modelo: el perímetro son 4 m, pero el patrón
        // calado agrega 28 m de trayectoria. Mismo material, otro costo de máquina.
        corteExtraM: 28
      }
    ],
    herrajes: [
      { sku: 'HER-PINTURA', cant: 1 },
      { sku: 'HER-TAQUETE', cant: 4 }
    ],
    procesos: [
      { proceso: 'pintura', minutos: 8 },
      { proceso: 'empaque', minutos: 3 }
    ]
  }
}

/** Marca visible en la interfaz: estos números no son del taller. */
export const SON_MUESTRA = true

/**
 * Piezas que tienen despiece pero **no están en la lista del taller**.
 *
 * La cajonera es el ejemplo que pidió Juan y no aparece en
 * `Catalogo_Conjunto_Taller_Proyectos.md`: la sección de Muebles llega hasta
 * credenzas y muebles de baño, pero no la incluye. Se da de alta aquí, marcada como
 * agregada, para que quede claro que es una propuesta y no algo que el taller ya
 * confirmó que fabrica.
 */
export const PRODUCTOS_MUESTRA = [
  {
    sku: 'TZ-CAJONERA-3', nombre: 'Cajonera de 3 cajones', cat: 'mue', unidad: 'pieza',
    descripcion: 'Ejemplo de captura de despiece. Pendiente de confirmar con el taller',
    vertical: 'V2', nivel: 3, fase: 'fase3', via: 'proyecto',
    porDefinir: true, precio: 0, tIns: 0, tCfg: 0, factor: 0, comp: [],
    taller: true, agregada: true
  }
]
